import React from 'react';
import { 
  Card, 
  Table, 
  Divider, 
  Descriptions, 
  Typography, 
  Space, 
  Tag, 
  Row, 
  Col,
  Image,
  Tooltip,
  Button
} from 'antd';
import { 
  AmazonOutlined, 
  BgColorsOutlined,
  DollarOutlined,
  ShoppingOutlined,
  TagOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import ProductImagesGallery from '../../../components/ProductImagesGallery';

const { Text, Paragraph, Link } = Typography;

const OrderItemsTable = ({ order }) => {
  const orderItemsColumns = [
    {
      title: 'Product',
      dataIndex: 'title',
      key: 'title',
      width: 400,
      render: (title, record) => (
        <Row gutter={12} align="middle">
          <Col>
            <ProductImagesGallery 
              skuItem={record.skuItem} 
              maxVisibleImages={1} 
              showRemainingCount={false} 
              imageSize={60} 
            />
          </Col>
          <Col flex="1">
            <div>
              <Link 
                href={`https://www.amazon.com/dp/${record.asin}`} 
                target="_blank"
                style={{ fontSize: '12px', color: '#1890ff' }}
              >
                <AmazonOutlined style={{ marginRight: 4 }} />
                {record.asin}
              </Link>
            </div>
            <Tooltip title={title}>
              <Paragraph 
                style={{ marginBottom: 4, marginTop: 4 }} 
                ellipsis={{ rows: 2 }}
                strong
              >
                {title || 'N/A'}
              </Paragraph>
            </Tooltip>
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Order Item ID: {record.orderItemId}
              </Text>
            </div>
          </Col>
        </Row>
      ),
    },
    {
      title: 'SKU',
      dataIndex: 'sellerSKU',
      key: 'sellerSKU',
      width: 150,
      render: (sku) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {sku || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Customization',
      key: 'customization',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (!record.customizationInfo) {
          return <Text type="secondary" style={{ fontSize: '11px' }}>None</Text>;
        }
        return (
          <Button
            size="small"
            icon={<BgColorsOutlined />}
            type="dashed"
          >
            View
          </Button>
        );
      },
    },
    {
      title: 'Status',
      key: 'statuses',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>Customization:</Text>
            <Tag 
              size="small" 
              color={getCustomizationStatusColor(record.customizationStatus)}
              style={{ marginLeft: 4, fontSize: '10px' }}
            >
              {record.customizationStatus || 'N/A'}
            </Tag>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>Fulfillment:</Text>
            <Tag 
              size="small" 
              color={getFulfillmentStatusColor(record.fulfillmentStatus)}
              style={{ marginLeft: 4, fontSize: '10px' }}
            >
              {record.fulfillmentStatus || 'N/A'}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: 'Printer',
      key: 'printer',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <div>
            <PrinterOutlined style={{ marginRight: 4, fontSize: '12px' }} />
            <Text style={{ fontSize: '12px' }}>
              {record.printer?.name || 'Not assigned'}
            </Text>
          </div>
          {record.baseCost && (
            <div>
              <DollarOutlined style={{ marginRight: 4, fontSize: '12px' }} />
              <Text style={{ fontSize: '12px' }}>
                ${parseFloat(record.baseCost).toFixed(2)}
              </Text>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantityOrdered',
      key: 'quantityOrdered',
      width: 80,
      align: 'center',
      render: (qty) => (
        <Tag color="blue">
          <ShoppingOutlined style={{ marginRight: 4 }} />
          {qty || 0}
        </Tag>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'itemPriceAmount',
      key: 'itemPriceAmount',
      width: 100,
      align: 'right',
      render: (price, record) => (
        <Space direction="vertical" size={2} style={{ textAlign: 'right' }}>
          <div>
            <Text strong style={{ fontSize: '13px' }}>
              ${price ? parseFloat(price).toFixed(2) : '0.00'}
            </Text>
          </div>
          {record.shippingPriceAmount && parseFloat(record.shippingPriceAmount) > 0 && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                +${parseFloat(record.shippingPriceAmount).toFixed(2)} shipping
              </Text>
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Earnings',
      dataIndex: 'itemEarnings',
      key: 'itemEarnings',
      width: 100,
      align: 'right',
      render: (earnings) => earnings ? (
        <Text strong style={{ color: '#52c41a', fontSize: '13px' }}>
          ${parseFloat(earnings).toFixed(2)}
        </Text>
      ) : (
        <Text type="secondary">-</Text>
      ),
    },
    {
      title: 'Total',
      key: 'subtotal',
      width: 100,
      align: 'right',
      render: (_, record) => {
        const itemTotal = record.itemPriceAmount && record.quantityOrdered 
          ? parseFloat(record.itemPriceAmount) * record.quantityOrdered 
          : 0;
        const shippingTotal = record.shippingPriceAmount ? parseFloat(record.shippingPriceAmount) : 0;
        const total = itemTotal + shippingTotal;
        
        return (
          <Text strong style={{ fontSize: '13px' }}>
            ${total.toFixed(2)}
          </Text>
        );
      },
    }
  ];

  // Helper functions for status colors
  const getCustomizationStatusColor = (status) => {
    const statusMap = {
      'NoCustomization': 'default',
      'TaskCreated': 'blue',
      'Doing': 'orange',
      'InternalReview': 'purple',
      'Rework': 'red',
      'MockupReady': 'cyan',
      'MockupSent': 'geekblue',
      'Confirming': 'gold',
      'CustomerConfirmed': 'green',
      'ConfirmedNoReply': 'lime',
      'AutoFulfill': 'green'
    };
    return statusMap[status] || 'default';
  };

  const getFulfillmentStatusColor = (status) => {
    const statusMap = {
      'Unfulfilled': 'default',
      'Fulfilled': 'blue',
      'Shipped': 'green',
      'Canceled': 'red'
    };
    return statusMap[status] || 'default';
  };

  // Calculate totals
  const subtotal = order.orderItems?.reduce((sum, item) => {
    const itemTotal = item.itemPriceAmount && item.quantityOrdered 
      ? parseFloat(item.itemPriceAmount) * item.quantityOrdered 
      : 0;
    return sum + itemTotal;
  }, 0) || 0;

  const shippingTotal = order.orderItems?.reduce((sum, item) => {
    return sum + (item.shippingPriceAmount ? parseFloat(item.shippingPriceAmount) : 0);
  }, 0) || 0;

  const totalEarnings = order.orderItems?.reduce((sum, item) => {
    return sum + (item.itemEarnings ? parseFloat(item.itemEarnings) : 0);
  }, 0) || 0;

  return (
    <Card 
      title={
        <Space>
          <ShoppingOutlined />
          <span>Order Items</span>
          <Tag color="blue">{order.orderItems?.length || 0} items</Tag>
        </Space>
      } 
      style={{ marginBottom: 24 }} 
      className="order-items-card"
    >
      <Table 
        columns={orderItemsColumns} 
        dataSource={order.orderItems?.map(item => ({ ...item, key: item.id }))} 
        pagination={false}
        className="order-items-table"
        size="middle"
        scroll={{ x: 1200 }}
      />
      
      <Divider style={{ margin: '16px 0' }} />
      
      {/* Order Summary */}
      <Row justify="end">
        <Col xs={24} sm={16} md={12} lg={8}>
          <Card 
            size="small" 
            className="order-total-summary"
            style={{ backgroundColor: '#ffffff' }}
          >
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item 
                label="Subtotal"
                labelStyle={{ fontWeight: 'normal' }}
              >
                <Text>${subtotal.toFixed(2)}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Shipping"
                labelStyle={{ fontWeight: 'normal' }}
              >
                <Text>${shippingTotal.toFixed(2)}</Text>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Tax"
                labelStyle={{ fontWeight: 'normal' }}
              >
                <Text>${(order.tax ? parseFloat(order.tax) : 0).toFixed(2)}</Text>
              </Descriptions.Item>
              
              <Divider style={{ margin: '8px 0' }} />
              
              <Descriptions.Item 
                label="Order Total"
                labelStyle={{ fontWeight: 'bold', fontSize: '14px' }}
              >
                <Text strong style={{ fontSize: '16px' }}>
                  ${(order.orderTotalAmount ? parseFloat(order.orderTotalAmount) : subtotal + shippingTotal).toFixed(2)}
                </Text>
              </Descriptions.Item>
              
              <Descriptions.Item 
                label="Total Earnings"
                labelStyle={{ fontWeight: 'bold', fontSize: '14px' }}
              >
                <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                  ${totalEarnings.toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default OrderItemsTable; 